import { useCallback, useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { useTranslation } from "react-i18next";

import {
  GetDemoRequestsCall,
  UpdateDemoRequestStatusCall,
  DeleteDemoRequestCall,
} from "../../redux/api/demoRequest.api";

const TIME_LABELS = {
  morning: "Morning (9am–12pm)",
  midday: "Midday (12pm–3pm)",
  afternoon: "Afternoon (3pm–6pm)",
  flexible: "Flexible",
};

const PILOT_LABELS = {
  yes: "Yes, I am interested",
  maybe: "Maybe later",
  not_now: "Not right now",
};

const DEFAULT_STATUS_KEYS = [
  "new_request",
  "contacted",
  "scheduled",
  "completed",
  "no_show",
  "rejected",
  "client",
];

const STATUS_LABELS_EN = {
  new_request: "New Request",
  contacted: "Contacted",
  scheduled: "Scheduled",
  completed: "Completed",
  no_show: "No Show",
  rejected: "Rejected",
  client: "Client",
};

const headCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "text.secondary",
  borderBottom: "1px solid",
  borderColor: "divider",
  bgcolor: "grey.50",
};

const bodyCellSx = {
  py: 2,
  px: 2,
  fontSize: "0.875rem",
  verticalAlign: "middle",
  borderColor: "divider",
};

/*
  demo_dates is a JSONB column on the backend (DemoRequest model) and
  the API doesn't enforce a fixed item shape. We've seen at least:
    - "2026-05-30"                            ← original mobile build
    - "2026-05-30T09:00:00.000Z"              ← ISO datetime
    - { date: "2026-05-30" }                  ← newer mobile build
    - { ymd: "2026-05-30", weekday: "Sat" }
    - { year: 2026, month: 5, day: 30 }
    - Date instances serialised to ISO
  Anything else (or null) becomes null and is dropped from the output
  rather than rendering as "[object Object]".
*/
function normalizeDemoDate(item) {
  if (item == null) return null;
  if (item instanceof Date) {
    const iso = item.toISOString();
    return iso.slice(0, 10);
  }
  if (typeof item === "string") {
    const s = item.trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const iso = s.length >= 10 ? s.slice(0, 10) : s;
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : s;
  }
  if (typeof item === "object") {
    const candidates = [
      item.date,
      item.ymd,
      item.day,
      item.value,
      item.iso,
      item.start,
      item.startDate,
    ];
    for (const c of candidates) {
      const norm = normalizeDemoDate(c);
      if (norm) return norm;
    }
    if (
      Number.isFinite(Number(item.year)) &&
      Number.isFinite(Number(item.month)) &&
      Number.isFinite(Number(item.day))
    ) {
      const y = String(item.year).padStart(4, "0");
      const m = String(item.month).padStart(2, "0");
      const d = String(item.day).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    return null;
  }
  return String(item);
}

function slotClock(item) {
  if (!item || typeof item !== "object") return "";
  const time = String(item.time || "").trim();
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return "";
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

function formatDates(demoDates) {
  if (!Array.isArray(demoDates) || demoDates.length === 0) return "—";
  const normalised = demoDates
    .map((item) => {
      const ymd = normalizeDemoDate(item);
      if (!ymd) return null;
      const clock = slotClock(item);
      return clock ? `${ymd} · ${clock}` : ymd;
    })
    .filter(Boolean);
  return normalised.length ? normalised.join(", ") : "—";
}

function formatDemoDatesPretty(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return "—";
  const out = arr
    .map((raw) => {
      const ymd = normalizeDemoDate(raw);
      if (!ymd) return null;
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
      if (!match) return ymd;
      const [, ys, ms, ds] = match;
      const dt = new Date(Number(ys), Number(ms) - 1, Number(ds));
      if (Number.isNaN(dt.getTime())) return ymd;
      const clock = slotClock(raw);
      const label = clock ? `${dt.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })} · ${clock}` : dt.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return label;
    })
    .filter(Boolean);
  return out.length ? out.join("; ") : "—";
}

function labelTime(v) {
  if (v == null || v === "") return "—";
  return TIME_LABELS[String(v)] || String(v);
}

/*
  `users.country` is stored as an ISO 3166-1 alpha-2 code (e.g. "IT",
  "IE"). We turn it into a flag emoji using regional-indicator code
  points — no extra package needed, works in every modern browser /
  OS that has flag emojis (everything except Windows native fonts).
  Returns an empty string for missing / malformed input.
*/
function isoToFlag(iso) {
  if (!iso || typeof iso !== "string") return "";
  const code = iso.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const base = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(
    base + code.charCodeAt(0) - a,
    base + code.charCodeAt(1) - a
  );
}

/*
  University display name: prefer the *current* linked university on
  the user (universityInfo) so admins always see the latest binding,
  fall back to the snapshot stored on the demo request row (which is
  whatever the user had at submission time).
*/
function universityDisplayName(row) {
  const live = row?.user?.universityInfo?.name;
  const snapshot = row?.university?.name;
  const typed = row?.university_name;
  return live || snapshot || typed || "";
}

function requesterName(row) {
  const user = row?.user;
  const fromForm = `${row?.first_name || ""} ${row?.last_name || ""}`.trim();
  const fromAccount = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return fromForm || fromAccount || "";
}

function requesterCountry(row) {
  return String(row?.country || row?.user?.country || "").trim();
}

function labelPilot(v) {
  if (v == null || v === "") return "—";
  return PILOT_LABELS[String(v)] || String(v);
}

function statusChipColor(key) {
  switch (key) {
    case "new_request":
      return "info";
    case "contacted":
      return "primary";
    case "scheduled":
      return "secondary";
    case "completed":
      return "success";
    case "no_show":
      return "warning";
    case "rejected":
      return "error";
    case "client":
      return "default";
    default:
      return "default";
  }
}

function flattenForDisplay(obj, prefix = "") {
  if (obj == null) return [];
  if (typeof obj !== "object") {
    return [{ key: prefix || "value", value: String(obj) }];
  }
  if (Array.isArray(obj)) {
    return [{ key: prefix || "items", value: JSON.stringify(obj) }];
  }
  const out = [];
  Object.keys(obj).forEach((k) => {
    const path = prefix ? `${prefix}.${k}` : k;
    const val = obj[k];
    if (val == null) {
      out.push({ key: path, value: "—" });
    } else if (typeof val === "object" && !Array.isArray(val)) {
      out.push(...flattenForDisplay(val, path));
    } else if (Array.isArray(val)) {
      out.push({ key: path, value: JSON.stringify(val) });
    } else {
      out.push({ key: path, value: String(val) });
    }
  });
  return out;
}

function DetailLine({ label, value }) {
  const display =
    value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <Grid container spacing={1} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {display}
        </Typography>
      </Grid>
    </Grid>
  );
}

export default function DemoRequestsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [statusKeys, setStatusKeys] = useState(DEFAULT_STATUS_KEYS);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    color: "success",
  });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const { rows: data, statuses } = await GetDemoRequestsCall();
    setRows(Array.isArray(data) ? data : []);
    if (Array.isArray(statuses) && statuses.length > 0) {
      setStatusKeys(statuses);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const paged = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const formatDt = (v) => (v ? moment(v).format("YYYY-MM-DD HH:mm") : "—");

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const showToast = (message, color = "success") => {
    setSnackbar({ show: true, message, color });
  };

  const handleSetStatus = async (status) => {
    if (!menuRow) return;
    closeMenu();
    const res = await UpdateDemoRequestStatusCall(menuRow.id, status);
    if (res?.success && res?.data) {
      setRows((prev) =>
        prev.map((r) => (r.id === menuRow.id ? { ...r, ...res.data } : r))
      );
      showToast(t("demoRequests.statusUpdated", "Status updated."));
    } else {
      showToast(
        t("demoRequests.statusError", "Could not update status."),
        "error"
      );
    }
  };

  const openDetails = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    closeMenu();
  };

  const confirmDelete = (row) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
    closeMenu();
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    const res = await DeleteDemoRequestCall(id);
    setDeleteOpen(false);
    setDeleteTarget(null);
    if (res?.success) {
      setRows((prev) => prev.filter((r) => r.id !== id));
      setDetailOpen(false);
      setDetailRow(null);
      showToast(t("demoRequests.deleted", "Request deleted."));
    } else {
      showToast(
        t("demoRequests.deleteError", "Could not delete."),
        "error"
      );
    }
  };

  const statusLabel = (key) =>
    t(`demoRequests.status.${key}`, STATUS_LABELS_EN[key] || key);

  const renderDetailContent = (row) => {
    if (!row) return null;
    const u = row.user;
    const submittedAt = row.created_at
      ? new Date(row.created_at).toISOString()
      : "—";
    const timePref = labelTime(row.time_preference);
    const pilot = labelPilot(row.pilot);
    const demoDatesPretty = formatDemoDatesPretty(row.demo_dates);
    const uniSnap = row.university;

    const liveUniversity = u?.universityInfo?.name || "";
    const universityForDetail =
      liveUniversity || uniSnap?.name || row.university_name || "—";
    const contactName = requesterName(row) || "—";
    const countryRaw = requesterCountry(row);
    const countryFlag = isoToFlag(countryRaw);
    const countryDisplay = countryRaw
      ? `${countryFlag ? `${countryFlag} ` : ""}${countryRaw}`
      : "—";

    const requestLines = [
      ["Name", contactName],
      ["Request ID", row.id],
      ["Submitted (UTC)", submittedAt],
      ["Updated (UTC)", row.updated_at ? new Date(row.updated_at).toISOString() : "—"],
      ["CRM status", statusLabel(row.status || "new_request")],
      ["Work email", row.work_email],
      ["Phone (form)", row.phone || "—"],
      ["Academic role", row.academic_role],
      ["Teaching area", row.teaching_area],
      ["Class size", row.class_size || "—"],
      ["Main interest", row.main_interest || "—"],
      ["Main goal", row.main_goal || "—"],
      ["Preferred dates (raw)", formatDates(row.demo_dates)],
      ["Preferred dates (formatted)", demoDatesPretty],
      ["Time preference", timePref],
      ["Pilot interest", pilot],
      ["Account role (at submit)", row.account_role || "—"],
      ["Country", countryDisplay],
      ["University", universityForDetail],
      ["University (snapshot at submit)", uniSnap?.name || "—"],
    ];

    return (
      <Stack spacing={3}>
        <Box>
          <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
            {t("demoRequests.detailRequestSection", "Demo request (same fields as admin email)")}
          </MDTypography>
          {requestLines.map(([k, v]) => (
            <DetailLine key={k} label={k} value={v} />
          ))}
        </Box>

        <Box>
          <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
            {t("demoRequests.detailAccountSection", "Microlab user profile")}
          </MDTypography>
          {u ? (
            flattenForDisplay(u).map(({ key, value }) => (
              <DetailLine key={key} label={key} value={value} />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("demoRequests.noUser", "No linked user.")}
            </Typography>
          )}
        </Box>

        {u?.universityInfo ? (
          <Box>
            <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
              {t("demoRequests.universitySection", "Linked university")}
            </MDTypography>
            <DetailLine label="Name" value={u.universityInfo.name} />
            <DetailLine label="Status" value={u.universityInfo.status} />
            <DetailLine
              label="License end"
              value={u.universityInfo.license_end ? formatDt(u.universityInfo.license_end) : "—"}
            />
          </Box>
        ) : null}

        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t(
              "demoRequests.detailFootnote",
              "User object includes linked university (universityInfo) when present."
            )}
          </Typography>
          <MDTypography variant="button" color="text" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
            {t("demoRequests.rawSection", "Raw record (JSON)")}
          </MDTypography>
          <Box
            component="pre"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "grey.100",
              fontSize: "0.72rem",
              overflow: "auto",
              maxHeight: 280,
              m: 0,
            }}
          >
            {JSON.stringify(row, null, 2)}
          </Box>
        </Box>
      </Stack>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <Box>
            <MDTypography variant="h4" fontWeight="bold" color="text" sx={{ letterSpacing: "-0.02em" }}>
              {t("demoRequests.title", "Demo requests")}
            </MDTypography>
            <MDTypography
              variant="body1"
              color="text"
              sx={{ mt: 1, maxWidth: 760, lineHeight: 1.6, opacity: 0.85 }}
            >
              {t(
                "demoRequests.subtitle",
                "Submissions from the mobile app “Request a demo” flow. Set status, open details, or delete."
              )}
            </MDTypography>
            {!loading ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("demoRequests.totalCount", { count: rows.length })}
              </Typography>
            ) : null}
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.35)"
                  : "0 12px 40px rgba(15, 23, 42, 0.06)",
            }}
          >
            <TableContainer>
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <CircularProgress size={36} />
                </Box>
              ) : (
                <Table size="medium" sx={{ minWidth: 1020 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>
                        {t("demoRequests.colSubmitted", "Submitted")}
                      </TableCell>
                      <TableCell sx={headCellSx}>
                        {t("demoRequests.colStatus", "Status")}
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 260 }}>
                        {t("demoRequests.colAccount", "Account")}
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 180 }}>
                        {t("demoRequests.colUniversity", "University")}
                      </TableCell>
                      <TableCell sx={headCellSx}>
                        {t("demoRequests.colRole", "Academic role")}
                      </TableCell>
                      <TableCell sx={headCellSx}>
                        {t("demoRequests.colArea", "Teaching area")}
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 200 }}>
                        {t(
                          "demoRequests.colDatesAndTime",
                          "Preferred dates & time"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...headCellSx,
                          width: 132,
                          position: "sticky",
                          right: 0,
                          zIndex: 2,
                          bgcolor: "grey.50",
                        }}
                        align="right"
                      >
                        {t("demoRequests.columns.actions", "Actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ ...bodyCellSx, py: 6 }}>
                          <Typography variant="body2" color="text.secondary" align="center">
                            {t("demoRequests.empty", "No demo requests yet.")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((row) => {
                        const u = row.user;
                        const namePart = requesterName(row);
                        const emailPart = u?.email || "";
                        const st = row.status || "new_request";
                        const countryRaw = requesterCountry(row);
                        const flag = isoToFlag(countryRaw);
                        const countryTitle = countryRaw
                          ? `${countryRaw}${flag ? ` ${flag}` : ""}`
                          : "";
                        const universityName = universityDisplayName(row);
                        return (
                          <TableRow
                            key={row.id}
                            hover
                            onClick={() => openDetails(row)}
                            sx={{
                              cursor: "pointer",
                              "&:last-child td": { borderBottom: 0 },
                            }}
                          >
                            <TableCell sx={bodyCellSx}>
                              <Typography variant="body2" fontWeight={500}>
                                {formatDt(row.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Chip
                                size="small"
                                label={statusLabel(st)}
                                color={statusChipColor(st)}
                                variant="filled"
                                sx={{
                                  fontWeight: 600,
                                  /*
                                    Force white text on every status chip.
                                    Also override the background for the
                                    "default" colour (used for `client`)
                                    because its native pale-grey fill
                                    would make white text unreadable.
                                  */
                                  color: "#fff",
                                  "& .MuiChip-label": { color: "#fff" },
                                  ...(statusChipColor(st) === "default" && {
                                    bgcolor: "grey.700",
                                    "&:hover": { bgcolor: "grey.800" },
                                  }),
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, maxWidth: 320 }}>
                              {u || row.work_email ? (
                                <Stack spacing={0.5}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {flag ? (
                                      <Typography
                                        component="span"
                                        sx={{
                                          fontSize: "1.6rem",
                                          lineHeight: 1,
                                          flexShrink: 0,
                                        }}
                                        title={countryTitle}
                                        aria-label={countryTitle}
                                      >
                                        {flag}
                                      </Typography>
                                    ) : null}
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={700}
                                      noWrap
                                      title={namePart}
                                      sx={{
                                        minWidth: 0,
                                        fontSize: "1rem",
                                        lineHeight: 1.25,
                                      }}
                                    >
                                      {namePart || "—"}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", wordBreak: "break-all", lineHeight: 1.4 }}
                                    >
                                      <Box component="span" sx={{ fontWeight: 700, color: "text.secondary" }}>
                                        Registered email:
                                      </Box>{" "}
                                      {emailPart || "—"}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", wordBreak: "break-all", lineHeight: 1.4 }}
                                    >
                                      <Box component="span" sx={{ fontWeight: 700, color: "text.secondary" }}>
                                        Work email:
                                      </Box>{" "}
                                      {row.work_email || "—"}
                                    </Typography>
                                    {row.phone ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", wordBreak: "break-all", lineHeight: 1.4 }}
                                      >
                                        <Box component="span" sx={{ fontWeight: 700, color: "text.secondary" }}>
                                          Phone:
                                        </Box>{" "}
                                        {row.phone}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, maxWidth: 220 }}>
                              {universityName ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  title={universityName}
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {universityName}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Typography variant="body2" color="text.secondary">
                                {row.academic_role || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  wordBreak: "break-word",
                                }}
                              >
                                {row.teaching_area || "—"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, whiteSpace: "normal", maxWidth: 260 }}>
                              <Stack spacing={0.25}>
                                <Typography variant="body2" color="text.primary" sx={{ wordBreak: "break-word" }}>
                                  {formatDates(row.demo_dates)}
                                </Typography>
                                {row.time_preference ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block" }}
                                  >
                                    {labelTime(row.time_preference)}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                ...bodyCellSx,
                                position: "sticky",
                                right: 0,
                                bgcolor: "background.paper",
                                zIndex: 1,
                                boxShadow: "-8px 0 12px rgba(15, 23, 42, 0.04)",
                              }}
                            >
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => openDetails(row)}
                                sx={{ fontWeight: 700, minWidth: 0, mr: 0.5 }}
                              >
                                {t("demoRequests.moreInfo", "Details")}
                              </Button>
                              <IconButton
                                size="small"
                                aria-label={t("demoRequests.actionsMenu", "Open row menu")}
                                onClick={(e) => openMenu(e, row)}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t("demoRequests.rowsPerPage", "Rows per page")}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                px: 2,
                py: 1,
              }}
            />
          </Paper>
        </Stack>
      </MDBox>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 2, mt: 0.5 } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ letterSpacing: "0.06em", textTransform: "uppercase" }}
          >
            {t("demoRequests.setStatus", "Set status")}
          </Typography>
        </Box>
        {statusKeys.map((key) => (
          <MenuItem
            key={key}
            dense
            selected={(menuRow?.status || "new_request") === key}
            onClick={() => handleSetStatus(key)}
          >
            {statusLabel(key)}
          </MenuItem>
        ))}
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", my: 0.5 }} />
        <MenuItem onClick={() => menuRow && openDetails(menuRow)}>
          {t("demoRequests.moreInfo", "More info")}
        </MenuItem>
        <MenuItem
          onClick={() => menuRow && confirmDelete(menuRow)}
          sx={{ color: "error.main" }}
        >
          {t("demoRequests.delete", "Delete")}
        </MenuItem>
      </Menu>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h5" fontWeight="bold">
            {t("demoRequests.detailTitle", "Request & account details")}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {renderDetailContent(detailRow)}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailOpen(false)}>
            {t("common.close", "Close")}
          </Button>
          {detailRow?.id ? (
            <Button color="error" variant="contained" onClick={() => confirmDelete(detailRow)}>
              {t("demoRequests.delete", "Delete")}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("demoRequests.deleteTitle", "Delete demo request?")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              "demoRequests.deleteBody",
              "This removes the row permanently. It cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>
            {t("demoRequests.cancel", "Cancel")}
          </Button>
          <Button color="error" variant="contained" onClick={doDelete}>
            {t("demoRequests.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={snackbar.color === "error" ? "error" : "success"}
        icon={snackbar.color === "error" ? "warning" : "check"}
        title={snackbar.color === "error" ? "Error" : "OK"}
        content={snackbar.message}
        dateTime=""
        open={snackbar.show}
        close={() => setSnackbar((s) => ({ ...s, show: false }))}
        onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
        bgWhite
      />

      <Footer />
    </DashboardLayout>
  );
}
